import { createFilterOptions, FilterOptionsState } from '@mui/material';
import {
  DeviceGroupDeviceModel,
  DeviceGroupModel
} from '@models/digital-signage/device-groups.model.ts';
import { DigitalSignageScreensModel } from '@modules/digital-signage/model/screens.model.ts';
import {
  PlaylistScreenApiObject,
  PlaylistScreenOption
} from '@modules/digital-signage/model/playlists.model.ts';
import {
  AssignedEntityEnum,
  DefaultContentOption,
  GroupSettingsFormValues,
  PreviewScreenParams,
  UseGroupSettingsFormParams
} from '@modules/digital-signage/model/group-settings.model.ts';
import { toDeviceOptions } from '@modules/digital-signage/utils/digital-signage.utils.ts';

export const getGroupSettingsDefaultValues = ({
  group,
  deviceId,
  deviceName
}: UseGroupSettingsFormParams): GroupSettingsFormValues => {
  const { defaultScreen, defaultPlaylist } = group ?? {};

  return {
    assignedScreen: defaultScreen
      ? {
          id: defaultScreen._id,
          title: defaultScreen.title,
          value: defaultScreen.title
        }
      : null,
    assignedPlaylist: defaultPlaylist
      ? {
          id: defaultPlaylist._id,
          title: defaultPlaylist.name,
          value: defaultPlaylist.name
        }
      : null,
    groupName: group?.name ?? '',
    // Without a group, the new one starts with this device in it.
    devices: group
      ? toDeviceOptions(group.devices)
      : deviceId
        ? [{ id: deviceId, title: deviceName ?? '', value: deviceName ?? '' }]
        : []
  };
};

export const getPreviewScreen = ({
  screensList,
  playlistsList,
  screen,
  playlist,
  lastSelected
}: PreviewScreenParams): DigitalSignageScreensModel | null => {
  const findScreen = (id?: string) =>
    screensList.find((item) => item._id === id) || null;

  const assignedScreen = screen ? findScreen(screen.id) : null;

  // The assigned playlist has priority over the assigned screen, unless the
  // user has just picked a screen to preview it.
  if (lastSelected !== AssignedEntityEnum.Screen && playlist) {
    const selectedPlaylist = playlistsList.find(
      (item) => item._id === playlist.id
    );

    const firstScreen = selectedPlaylist?.screens?.[0]?.screen as
      | PlaylistScreenApiObject
      | string
      | undefined;

    return (
      findScreen(
        typeof firstScreen === 'string' ? firstScreen : firstScreen?._id
      ) ?? assignedScreen
    );
  }

  return assignedScreen;
};

export const getGroupDeviceIds = (group?: DeviceGroupModel | null): string[] =>
  (group?.devices ?? []).map((device) => device.deviceId).filter(Boolean);

export const filterDevicesByQuery = (
  devices: DeviceGroupDeviceModel[],
  query: string
): DeviceGroupDeviceModel[] => {
  const search = query.trim().toLowerCase();
  return devices.filter(
    (device) =>
      (device.name ?? '').toLowerCase().includes(search) ||
      (device.deviceId ?? '').toLowerCase().includes(search)
  );
};

export const toggleDeviceOption = (
  selected: PlaylistScreenOption[],
  device: DeviceGroupDeviceModel
): PlaylistScreenOption[] =>
  selected.some((option) => option.id === device._id)
    ? selected.filter((option) => option.id !== device._id)
    : [...selected, ...toDeviceOptions([device])];
export const CONTENT_TYPE_LABELS: Record<AssignedEntityEnum, string> = {
  [AssignedEntityEnum.Screen]: 'digital_signage_content_type_screen',
  [AssignedEntityEnum.Playlist]: 'digital_signage_content_type_playlist'
};

export const CONTENT_GROUP_LABELS: Record<AssignedEntityEnum, string> = {
  [AssignedEntityEnum.Screen]: 'digital_signage_content_group_screens',
  [AssignedEntityEnum.Playlist]: 'digital_signage_content_group_playlists'
};

export const CONTENT_TYPE_CHIP_CLASSES: Record<AssignedEntityEnum, string> = {
  [AssignedEntityEnum.Screen]: 'bg-(--blue-alpha-100)! text-(--blue)!',
  [AssignedEntityEnum.Playlist]: 'bg-(--primary-100)! text-(--primary)!'
};

const withType =
  (type: AssignedEntityEnum) =>
  (option: PlaylistScreenOption): DefaultContentOption => ({ ...option, type });

export const toDefaultContentOptions = (
  screens: PlaylistScreenOption[],
  playlists: PlaylistScreenOption[]
): DefaultContentOption[] => [
  ...screens.map(withType(AssignedEntityEnum.Screen)),
  ...playlists.map(withType(AssignedEntityEnum.Playlist))
];

export const getDefaultContentValue = (
  screen: PlaylistScreenOption | null,
  playlist: PlaylistScreenOption | null
): DefaultContentOption | null => {
  if (playlist) return withType(AssignedEntityEnum.Playlist)(playlist);
  if (screen) return withType(AssignedEntityEnum.Screen)(screen);
  return null;
};

export const toAssignedValues = (
  option: DefaultContentOption | null
): Pick<GroupSettingsFormValues, 'assignedScreen' | 'assignedPlaylist'> => {
  const entity = option
    ? { id: option.id, title: option.title, value: option.value }
    : null;

  return {
    assignedScreen: option?.type === AssignedEntityEnum.Screen ? entity : null,
    assignedPlaylist:
      option?.type === AssignedEntityEnum.Playlist ? entity : null
  };
};

export const isSameDefaultContent = (
  option: DefaultContentOption,
  value: DefaultContentOption
): boolean => option.id === value.id && option.type === value.type;

const filterContentOptions = createFilterOptions<DefaultContentOption>();

export const filterDefaultContentOptions = (
  options: DefaultContentOption[],
  state: FilterOptionsState<DefaultContentOption>
): DefaultContentOption[] => {
  const filtered = filterContentOptions(options, state);
  const title = state.inputValue.trim();
  const exists = options.some(
    (option) => option.title.toLowerCase() === title.toLowerCase()
  );

  return title && !exists
    ? [
        ...filtered,
        {
          id: '',
          title,
          value: title,
          type: AssignedEntityEnum.Screen,
          isCreate: true
        }
      ]
    : filtered;
};
